import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useAppSelector } from "../../hooks/hooks";
import { updateElementValue } from "../../utils/updateElementValue";
import { RootState } from "../store";
import { doFetch } from "../../utils/doFetch";
import { LoadingType } from "../../types";

interface ApplicationData {
  applicationVersion : string;
  applicationNote  : string;
  maintenanceNote : string;
}

interface CommonState {
  loadingCount: number;
  showPopup: boolean;
  showSavingGif: boolean;
  concurrentPopup : boolean;
  concurrentMsg : string;
  pdfgenerationerror: boolean;
  pdfgenerationErrorMsg: string;
  docGenerateError: boolean;
  docGenerateErrorMsg: any;
  commonStatus: LoadingType;
  commonError?: string,
  applicationData : ApplicationData | null,
}

const initialState = {
  loadingCount: 0,
  showPopup: false,
  showSavingGif: false,
  concurrentPopup : false,
  concurrentMsg : "No messages",
  pdfgenerationerror: false,
  pdfgenerationErrorMsg: "No message",
  docGenerateError: false,
  docGenerateErrorMsg: undefined,
  commonStatus: "idle",
  commonError : "",
  applicationData: null,
} as CommonState;

export const getApplicationData = createAsyncThunk<
  any,
  undefined,
  { state: RootState; rejectValue: string }
>("users/getApplicationData", async (_, { rejectWithValue, dispatch }) => {
  dispatch(showHideLoadingGif(true));
  try {
    const reponse = await doFetch({
      url: `authenticateservice/getApplicationData`,
    });

    if (reponse.ok) {
      dispatch(showHideLoadingGif(false));
      return reponse.json();
    } else {
      dispatch(showHideLoadingGif(false));
      return rejectWithValue(
        "Error while fetching Application Data"
      );
    }
  } catch (error) {
    dispatch(showHideLoadingGif(false));
    return rejectWithValue("Error while fetching Application Data");
  }
});

export const commonSlice = createSlice({
  name: "common",
  initialState,
  reducers: {
    showHideLoadingGif: (
      state: CommonState,
      action: PayloadAction<boolean>
    ) => {
      if (action.payload) {
        state.loadingCount += 1;
        state.showPopup = true;
      } else {
        if (state.loadingCount === 1) {
          state.loadingCount = 0;
          state.showPopup = false;
        } else if (state.loadingCount > 0){
          state.loadingCount = state.loadingCount - 1;
        }
      }
    },
    showHideSavingGif: (state: CommonState, action: PayloadAction<boolean>) => {
      state.showSavingGif = action.payload;
    },
    updateElementValueInCommonReducer: updateElementValue,
    updatePdfGenerationError: (
      state: CommonState,
      action: PayloadAction<{ error: boolean; errorMsg: string }>
    ) => {
      state.pdfgenerationerror = action.payload.error;
      state.pdfgenerationErrorMsg = action.payload.errorMsg;
    },
    updateDocGenerateError: (
      state: CommonState,
      action: PayloadAction<{ error: boolean; errorMsg: any }>
    ) => {
      state.docGenerateError = action.payload.error;
      state.docGenerateErrorMsg = action.payload.errorMsg;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getApplicationData.pending, (state) => {
        state.commonStatus = "loading";
        state.commonError = undefined;
      })
      .addCase(getApplicationData.fulfilled, (state, action) => {
        state.commonStatus = "success";
        state.applicationData = action.payload;
      })
      .addCase(getApplicationData.rejected, (state, action) => {
        state.commonStatus = "error";
        state.commonError = action.payload;
      })    
    }
});

export const {
  showHideLoadingGif,
  showHideSavingGif,
  updateElementValueInCommonReducer,
  updatePdfGenerationError,
  updateDocGenerateError,
} = commonSlice.actions;

export const  useCommonReducer = () => useAppSelector((state) => state.common);

export default commonSlice.reducer;
